import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { API_URL } from '../../../config';
import { toApiError } from '../../../lib/api-error';
import type {
  AgentTask,
  AgentTaskDetailResponse,
  AgentTaskTriggerType,
  TaskCommentsResponse,
} from '../types';

export function useSubtasks(parentTaskId: string | undefined) {
  return useQuery({
    queryKey: ['subtasks', parentTaskId],
    queryFn: async (): Promise<{ ok: boolean; data: AgentTask[] }> => {
      const res = await fetch(`${API_URL}/tasks/${parentTaskId}/subtasks`, {
        credentials: 'include',
      });
      if (!res.ok) throw await toApiError(res, 'Failed to fetch subtasks');
      return res.json();
    },
    enabled: !!parentTaskId,
  });
}

export function useTask(id: string | undefined) {
  return useQuery({
    queryKey: ['task', id],
    queryFn: async (): Promise<AgentTaskDetailResponse> => {
      const res = await fetch(`${API_URL}/tasks/${id}`, {
        credentials: 'include',
      });
      if (!res.ok) throw await toApiError(res, 'Failed to fetch task');
      return res.json();
    },
    enabled: !!id,
  });
}

function invalidateTaskQueries(queryClient: ReturnType<typeof useQueryClient>, id?: string) {
  queryClient.invalidateQueries({ queryKey: ['tasks'] });
  queryClient.invalidateQueries({ queryKey: ['subtasks'] });
  if (id) {
    queryClient.invalidateQueries({ queryKey: ['task', id] });
  }
}

export function useCompleteTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, resolutionCommit }: { id: string; resolutionCommit?: string }) => {
      const res = await fetch(`${API_URL}/tasks/${id}/complete`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ resolutionCommit }),
      });
      if (!res.ok) throw await toApiError(res, 'Failed to complete task');
      return res.json();
    },
    onSuccess: (_data, variables) => {
      invalidateTaskQueries(queryClient, variables.id);
    },
  });
}

export function useCancelTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`${API_URL}/tasks/${id}/cancel`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) throw await toApiError(res, 'Failed to cancel task');
      return res.json();
    },
    onSuccess: (_data, id) => {
      invalidateTaskQueries(queryClient, id);
    },
  });
}

export function useTriggerTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`${API_URL}/tasks/${id}/trigger`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) throw await toApiError(res, 'Failed to trigger task');
      return res.json();
    },
    onSuccess: (_data, id) => {
      invalidateTaskQueries(queryClient, id);
    },
  });
}

export function useReopenTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status: 'actionable' | 'in_progress' }) => {
      const res = await fetch(`${API_URL}/tasks/${id}/reopen`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      });
      if (!res.ok) throw await toApiError(res, 'Failed to reopen task');
      return res.json();
    },
    onSuccess: (_data, variables) => {
      invalidateTaskQueries(queryClient, variables.id);
    },
  });
}

export function useReleaseTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status: 'actionable' | 'waiting' }) => {
      const res = await fetch(`${API_URL}/tasks/${id}/release`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      });
      if (!res.ok) throw await toApiError(res, 'Failed to release task');
      return res.json();
    },
    onSuccess: (_data, variables) => {
      invalidateTaskQueries(queryClient, variables.id);
    },
  });
}

export function useClaimTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`${API_URL}/tasks/${id}/claim`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) throw await toApiError(res, 'Failed to claim task');
      return res.json();
    },
    onSuccess: (_data, id) => {
      invalidateTaskQueries(queryClient, id);
    },
  });
}

/**
 * Manual kanban reorder within the task's current status column. No
 * hook-level invalidation/optimistic update — the kanban board orchestrates
 * the optimistic splice and settle itself (per-call callbacks).
 */
export function useMoveTask() {
  return useMutation({
    mutationFn: async ({
      id,
      beforeItemId,
      afterItemId,
    }: {
      id: string;
      beforeItemId: string | null;
      afterItemId: string | null;
    }) => {
      const res = await fetch(`${API_URL}/tasks/${id}/move`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ beforeItemId, afterItemId }),
      });
      if (!res.ok) throw await toApiError(res, 'Failed to move task');
      return res.json();
    },
  });
}

interface UpdateTaskPayload {
  title?: string;
  description?: string;
  assignedTo?: string | null;
  priority?: number;
  tags?: string[];
}

export function useUpdateTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...payload }: UpdateTaskPayload & { id: string }) => {
      const res = await fetch(`${API_URL}/tasks/${id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw await toApiError(res, 'Failed to update task');
      return res.json();
    },
    onSuccess: (_data, variables) => {
      invalidateTaskQueries(queryClient, variables.id);
    },
  });
}

export function useSetTaskTags() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, tagIds }: { id: string; tagIds: string[] }) => {
      const res = await fetch(`${API_URL}/org/tags/entity`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ entityType: 'card', entityId: id, tagIds }),
      });
      if (!res.ok) throw await toApiError(res, 'Failed to update task tags');
      return res.json();
    },
    onSuccess: (_data, variables) => {
      invalidateTaskQueries(queryClient, variables.id);
      queryClient.invalidateQueries({ queryKey: ['entity-tags', 'card', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['tags'] });
    },
  });
}

export function useDeleteTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`${API_URL}/tasks/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) throw await toApiError(res, 'Failed to delete task');
      return res.json();
    },
    onSuccess: () => {
      invalidateTaskQueries(queryClient);
    },
  });
}

export function useAddCustomer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      taskId,
      orgId,
      name,
      accountCardId,
    }: {
      taskId: string;
      orgId?: string;
      name?: string;
      accountCardId?: string;
    }) => {
      const res = await fetch(`${API_URL}/tasks/${taskId}/customers`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ orgId, name, accountCardId }),
      });
      if (!res.ok) throw await toApiError(res, 'Failed to add customer');
      return res.json();
    },
    onSuccess: (_data, variables) => {
      invalidateTaskQueries(queryClient, variables.taskId);
      queryClient.invalidateQueries({ queryKey: ['known-customers'] });
    },
  });
}

export function useUpdateCustomer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      taskId,
      customerId,
      customerOrgId,
    }: {
      taskId: string;
      customerId: string;
      customerOrgId: string | null;
    }) => {
      const res = await fetch(`${API_URL}/tasks/${taskId}/customers/${customerId}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customerOrgId }),
      });
      if (!res.ok) throw await toApiError(res, 'Failed to update customer');
      return res.json();
    },
    onSuccess: (_data, variables) => {
      invalidateTaskQueries(queryClient, variables.taskId);
      queryClient.invalidateQueries({ queryKey: ['known-customers'] });
    },
  });
}

export function useRemoveCustomer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ taskId, customerId }: { taskId: string; customerId: string }) => {
      const res = await fetch(`${API_URL}/tasks/${taskId}/customers/${customerId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) throw await toApiError(res, 'Failed to remove customer');
      return res.json();
    },
    onSuccess: (_data, variables) => {
      invalidateTaskQueries(queryClient, variables.taskId);
      queryClient.invalidateQueries({ queryKey: ['known-customers'] });
    },
  });
}

interface CreateTaskPayload {
  propertyGithubRepoId: string;
  title: string;
  description: string;
  triggerType: AgentTaskTriggerType;
  triggerConfig?: Record<string, unknown>;
  tags?: string[];
  priority?: number;
  parentTaskId?: string;
}

export function useCreateTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: CreateTaskPayload) => {
      const res = await fetch(`${API_URL}/tasks`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw await toApiError(res, 'Failed to create task');
      return res.json();
    },
    onSuccess: () => {
      invalidateTaskQueries(queryClient);
    },
  });
}

export function useTaskComments(taskId: string | undefined, page: number = 1) {
  return useQuery({
    queryKey: ['task-comments', taskId, page],
    queryFn: async (): Promise<TaskCommentsResponse> => {
      const res = await fetch(`${API_URL}/tasks/${taskId}/comments?page=${page}&pageSize=25`, {
        credentials: 'include',
      });
      if (!res.ok) throw await toApiError(res, 'Failed to fetch comments');
      return res.json();
    },
    enabled: !!taskId,
  });
}

export function useCreateTaskComment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ taskId, body }: { taskId: string; body: string }) => {
      const res = await fetch(`${API_URL}/tasks/${taskId}/comments`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ body }),
      });
      if (!res.ok) throw await toApiError(res, 'Failed to post comment');
      return res.json();
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['task-comments', variables.taskId] });
    },
  });
}
